import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { loadFromStorage, saveToStorage, storageKeys } from '../utils/localStorage';

const CartContext = createContext(null);

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'HYDRATE':
      return action.payload;
    case 'CLEAR_NOTICE':
      return { ...state, notice: null };
    case 'ADD_ITEM': {
      const maxStock = Number(action.payload.maxStock ?? 0);
      const id = action.payload.id;

      if (maxStock <= 0) {
        return {
          ...state,
          notice: { id, message: 'Sold Out', type: 'error' },
        };
      }

      const addQty = action.payload.quantity || 1;
      const existing = state.items.find((i) => i.id === id);
      const currentQty = existing ? existing.quantity : 0;
      const desiredQty = currentQty + addQty;

      if (desiredQty > maxStock) {
        const cappedQty = maxStock;
        const notice = {
          id,
          message:
            currentQty >= maxStock
              ? `Only ${maxStock} in stock`
              : `Only ${maxStock} available — quantity adjusted`,
          type: 'warning',
        };
        if (existing) {
          return {
            ...state,
            notice,
            items: state.items.map((i) =>
              i.id === id ? { ...i, quantity: cappedQty, maxStock } : i
            ),
          };
        }
        return {
          ...state,
          notice,
          items: [
            ...state.items,
            { ...action.payload, quantity: cappedQty, maxStock },
          ],
        };
      }

      if (existing) {
        return {
          ...state,
          notice: null,
          items: state.items.map((i) =>
            i.id === id
              ? { ...i, quantity: desiredQty, maxStock }
              : i
          ),
        };
      }
      return {
        ...state,
        notice: null,
        items: [...state.items, { ...action.payload, quantity: addQty, maxStock }],
      };
    }
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((i) => i.id !== action.payload), notice: null };
    case 'UPDATE_QUANTITY': {
      const maxStock = Number(action.payload.maxStock ?? Infinity);
      const id = action.payload.id;
      const requested = action.payload.quantity;

      if (requested <= 0) {
        return { ...state, items: state.items.filter((i) => i.id !== id), notice: null };
      }

      if (maxStock <= 0) {
        return {
          ...state,
          items: state.items.filter((i) => i.id !== id),
          notice: { id, message: 'Sold Out — removed from cart', type: 'error' },
        };
      }

      const capped = Math.min(requested, maxStock);
      const notice =
        requested > maxStock
          ? { id, message: `Only ${maxStock} in stock`, type: 'warning' }
          : null;

      return {
        ...state,
        notice,
        items: state.items
          .map((i) =>
            i.id === id ? { ...i, quantity: Math.max(1, capped), maxStock } : i
          )
          .filter((i) => i.quantity > 0),
      };
    }
    case 'SYNC_STOCK': {
      const stockMap = action.payload?.stock || action.payload;
      const priceMap = action.payload?.prices;
      let notice = null;
      let changed = false;
      const items = state.items
        .map((item) => {
          const stock = stockMap[item.id];
          const nextPrice = priceMap?.[item.id];
          const priceChanged = nextPrice !== undefined && nextPrice !== item.price;
          if (stock === undefined && !priceChanged) return item;
          if (stock !== undefined && stock <= 0) {
            changed = true;
            notice = { id: item.id, message: `${item.title} is Sold Out`, type: 'error' };
            return null;
          }
          let next = item;
          if (stock !== undefined && (item.quantity > stock || item.maxStock !== stock)) {
            if (item.quantity > stock) {
              notice = {
                id: item.id,
                message: `Only ${stock} left for ${item.title}`,
                type: 'warning',
              };
            }
            changed = true;
            next = { ...next, quantity: Math.min(item.quantity, stock), maxStock: stock };
          }
          if (priceChanged) {
            changed = true;
            next = { ...next, price: nextPrice };
          }
          return next;
        })
        .filter(Boolean);
      if (!changed) return state;
      return { ...state, items, notice };
    }
    case 'CLEAR_CART':
      return { ...state, items: [], notice: null };
    case 'TOGGLE_DRAWER':
      return { ...state, isDrawerOpen: action.payload };
    case 'TOGGLE_WISHLIST': {
      const wishId = action.payload;
      const isWishlisted = state.wishlist.includes(wishId);
      return {
        ...state,
        wishlist: isWishlisted
          ? state.wishlist.filter((w) => w !== wishId)
          : [...state.wishlist, wishId],
      };
    }
    default:
      return state;
  }
};

const initialState = {
  items: [],
  wishlist: [],
  isDrawerOpen: false,
  notice: null,
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  useEffect(() => {
    const savedCart = loadFromStorage(storageKeys.cart, { items: [] });
    const savedWishlist = loadFromStorage(storageKeys.wishlist, []);
    dispatch({
      type: 'HYDRATE',
      payload: { ...initialState, items: savedCart.items || [], wishlist: savedWishlist },
    });
  }, []);

  useEffect(() => {
    saveToStorage(storageKeys.cart, { items: state.items });
  }, [state.items]);

  useEffect(() => {
    saveToStorage(storageKeys.wishlist, state.wishlist);
  }, [state.wishlist]);

  useEffect(() => {
    if (!state.notice) return undefined;
    const timer = setTimeout(() => dispatch({ type: 'CLEAR_NOTICE' }), 4000);
    return () => clearTimeout(timer);
  }, [state.notice]);

  const addToCart = useCallback((product, quantity = 1) => {
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        id: product.id,
        title: product.title,
        price: product.price,
        image: product.images[0],
        slug: product.slug,
        quantity,
        maxStock: product.stock,
      },
    });
  }, []);

  const removeFromCart = useCallback((id) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  }, []);

  const updateQuantity = useCallback((id, quantity, maxStock) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity, maxStock } });
  }, []);

  const syncCartStock = useCallback((stockMap) => {
    dispatch({ type: 'SYNC_STOCK', payload: stockMap });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
  }, []);

  const clearNotice = useCallback(() => {
    dispatch({ type: 'CLEAR_NOTICE' });
  }, []);

  const toggleDrawer = useCallback((open) => {
    dispatch({ type: 'TOGGLE_DRAWER', payload: open });
  }, []);

  const toggleWishlist = useCallback((id) => {
    dispatch({ type: 'TOGGLE_WISHLIST', payload: id });
  }, []);

  const itemCount = state.items.reduce((sum, i) => sum + i.quantity, 0);
  const isWishlisted = useCallback(
    (id) => state.wishlist.includes(id),
    [state.wishlist]
  );

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        wishlist: state.wishlist,
        isDrawerOpen: state.isDrawerOpen,
        notice: state.notice,
        itemCount,
        addToCart,
        removeFromCart,
        updateQuantity,
        syncCartStock,
        clearCart,
        clearNotice,
        toggleDrawer,
        toggleWishlist,
        isWishlisted,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
